import { buildLocationKey, matchSavedLocationId } from "../../components/common/SavedLocationSelector";
import { formatDateTime as formatCompanyDateTime, toInputDateTime as toCompanyInputDateTime, toIsoString as toCompanyIsoString } from "../../lib/dateTime";
import { normalizeSavedLoadStatuses, normalizeVisibleStatus } from "../../lib/statuses";

export const DEFAULT_PAGE_SIZE = 30;

export const createStop = (stopType = "pickup") => ({
  location_id: "",
  original_location_id: "",
  original_location_key: "",
  auto_round_trip_time: false,
  stop_type: stopType,
  company_name: "",
  contact_name: "",
  phone: "",
  address: "",
  city: "",
  state_province: "",
  postal_code: "",
  country: "Canada",
  planned_datetime: "",
  actual_arrived_at: "",
  actual_departed_at: "",
  truck_id: "",
  trailer_id: "",
  driver_id: "",
  notes: "",
});

export const blankLoad = {
  load_number: "",
  is_round_trip: false,
  customer_reference_number: "",
  customer_id: "",
  rate: "",
  currency: "CAD",
  notes: "",
  stops: [createStop("pickup"), createStop("delivery")],
};

export const blankRepeatForm = {
  mode: "create_now",
  template_name: "",
  start_date: new Date().toISOString().slice(0, 10),
  copies_count: 1,
  interval_days: 1,
};

export const toDateInputValue = (value) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildDefaultLoadsDateRange = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - 7);
  const end = new Date(today);
  end.setDate(today.getDate() + 7);
  return {
    start_date: toDateInputValue(start),
    end_date: toDateInputValue(end),
  };
};

export const buildDefaultLoadFilters = () => ({
  customer_id: "",
  status: [],
  shipment_direction: "",
  truck_id: "",
  trailer_id: "",
  driver_id: "",
  ...buildDefaultLoadsDateRange(),
  sort_by: "load_number",
  sort_dir: "asc",
});

export const LOAD_STATUS_FILTER_OPTIONS = ["Unassigned", "Assigned", "In Transit", "Delivered", "Invoiced", "Canceled"];

export const normalizeLoadFilterState = (filters) => ({
  ...filters,
  status: normalizeSavedLoadStatuses(filters?.status || []),
});

export const statusFilterLabel = (selectedStatuses) => {
  const visibleStatuses = normalizeSavedLoadStatuses(selectedStatuses);
  if (!visibleStatuses.length) {
    return "All statuses";
  }
  if (visibleStatuses.length <= 2) {
    return visibleStatuses.map((value) => normalizeVisibleStatus(value)).join(", ");
  }
  return `${visibleStatuses.length} statuses selected`;
};

export const repeatGapOptions = [1, 2, 3, 4, 5, 6, 7];

export const formatMoney = (value, currency) => new Intl.NumberFormat("en-CA", { style: "currency", currency, maximumFractionDigits: 2 }).format(value || 0);

export const formatDistance = (miles, unit = "Miles") => {
  if (miles == null) {
    return "Distance pending";
  }
  const displayValue = unit === "Kilometers" ? miles * 1.60934 : miles;
  return `${displayValue.toLocaleString("en-CA", { maximumFractionDigits: 1, minimumFractionDigits: 1 })} ${unit === "Kilometers" ? "km" : "mi"}`;
};

export const formatDateTime = (value) => formatCompanyDateTime(value);

export const toInputDateTime = (value) => toCompanyInputDateTime(value);

export const toIsoString = (value) => toCompanyIsoString(value);

export const originalRoutePickupLabel = (load) => load.invoice_pickup_company || load.pickup_company || load.stops?.[0]?.company_name || "Pickup A";

export const originalRouteDeliveryLabel = (load) => load.invoice_delivery_company || load.delivery_company || load.stops?.find((stop, index) => index > 0 && ["delivery", "drop"].includes(stop.stop_type))?.company_name || load.stops?.[1]?.company_name || "Delivery B";

export const originalRouteLabel = (load) => `${originalRoutePickupLabel(load)} → ${originalRouteDeliveryLabel(load)}`;

const normalizeCountry = (value) => (value || "").trim().toLowerCase();

export const shipmentDirectionLabel = (originCountry, destinationCountry) => {
  const origin = normalizeCountry(originCountry);
  const destination = normalizeCountry(destinationCountry);
  if (origin === "canada" && ["usa", "us", "united states", "united states of america"].includes(destination)) {
    return "Canada to USA";
  }
  if (["usa", "us", "united states", "united states of america"].includes(origin) && destination === "canada") {
    return "USA to Canada";
  }
  return "Other";
};

const normalizeText = (value) => (value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

export const compactCustomerName = (customerName, billingAddress = "") => {
  if (!customerName) {
    return "—";
  }
  if (billingAddress && normalizeText(customerName).includes(normalizeText(billingAddress))) {
    return customerName.replace(/\s*[-–]\s*.*/, "").trim();
  }
  return customerName;
};

export const loadShipmentIds = (load) => (load.shipments?.length ? load.shipments.map((item) => item.shipment_id).join(", ") : load.shipment_id || "Auto after save");

export const loadShipmentDirections = (load) => {
  const directions = load.shipments?.length ? [...new Set(load.shipments.map((item) => item.shipment_direction).filter(Boolean))] : [load.shipment_direction].filter(Boolean);
  return directions.length ? directions.join(" • ") : "Pending";
};

const sortIndicator = (active, direction) => (active ? (direction === "asc" ? "↑" : "↓") : "↕");

export const SortHeaderButton = ({ label, sortKey, filters, onToggle, testId }) => (
  <button
    className={`inline-flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-[0.14em] ${filters.sort_by === sortKey ? "text-zinc-900" : "text-zinc-500"}`}
    data-testid={testId}
    onClick={() => onToggle(sortKey)}
    type="button"
  >
    <span>{label}</span>
    <span aria-hidden="true">{sortIndicator(filters.sort_by === sortKey, filters.sort_dir)}</span>
  </button>
);

export const labelizeStopType = (value) => {
  if (value === "drop_hook") {
    return "Drop & Hook";
  }
  return value ? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) : "Stop";
};

export const mapLoadStops = (load, locations = []) => {
  if (load.stops?.length) {
    return load.stops.map((stop) => ({
      ...createStop(stop.stop_type),
      ...stop,
      location_id: matchSavedLocationId(stop, locations),
      original_location_id: matchSavedLocationId(stop, locations),
      original_location_key: buildLocationKey(stop),
      auto_round_trip_time: false,
      contact_name: stop.contact_name || "",
      phone: stop.phone || "",
      postal_code: stop.postal_code || "",
      notes: stop.notes || "",
      truck_id: stop.truck_id || "",
      trailer_id: stop.trailer_id || "",
      driver_id: stop.driver_id || "",
      planned_datetime: toInputDateTime(stop.planned_datetime),
      actual_arrived_at: toInputDateTime(stop.actual_arrived_at),
      actual_departed_at: toInputDateTime(stop.actual_departed_at),
    }));
  }

  return [
    {
      ...createStop("pickup"),
      location_id: "",
      original_location_id: "",
      original_location_key: buildLocationKey({
        company_name: load.pickup_company || "",
        address: load.pickup_address || "",
        city: load.pickup_city || "",
        state_province: load.pickup_state_province || "",
        country: load.pickup_country || "",
      }),
      company_name: load.pickup_company || "",
      contact_name: load.pickup_contact || "",
      phone: load.pickup_phone || "",
      address: load.pickup_address || "",
      city: load.pickup_city || "",
      state_province: load.pickup_state_province || "",
      postal_code: load.pickup_postal_code || "",
      country: load.pickup_country || "Canada",
      planned_datetime: toInputDateTime(load.pickup_datetime),
      actual_arrived_at: toInputDateTime(load.pickup_actual_arrived_at),
      actual_departed_at: toInputDateTime(load.pickup_actual_departed_at),
    },
    {
      ...createStop("delivery"),
      location_id: "",
      original_location_id: "",
      original_location_key: buildLocationKey({
        company_name: load.delivery_company || "",
        address: load.delivery_address || "",
        city: load.delivery_city || "",
        state_province: load.delivery_state_province || "",
        country: load.delivery_country || "",
      }),
      company_name: load.delivery_company || "",
      contact_name: load.delivery_contact || "",
      phone: load.delivery_phone || "",
      address: load.delivery_address || "",
      city: load.delivery_city || "",
      state_province: load.delivery_state_province || "",
      postal_code: load.delivery_postal_code || "",
      country: load.delivery_country || "Canada",
      planned_datetime: toInputDateTime(load.delivery_datetime),
      actual_arrived_at: toInputDateTime(load.delivery_actual_arrived_at),
      actual_departed_at: toInputDateTime(load.delivery_actual_departed_at),
    },
  ];
};

export const buildRoundTripStops = (stops) => {
  const pickup = { ...createStop("pickup"), ...(stops[0] || {}), stop_type: "pickup" };
  const delivery = { ...createStop("delivery"), ...(stops[1] || createStop("delivery")), stop_type: "delivery" };
  const pickupDate = pickup.planned_datetime ? new Date(toIsoString(pickup.planned_datetime)) : null;
  const deliveryDate = delivery.planned_datetime ? new Date(toIsoString(delivery.planned_datetime)) : null;
  const outboundMs = pickupDate && deliveryDate ? Math.max(deliveryDate.getTime() - pickupDate.getTime(), 0) : 0;
  const returnPickupTime = delivery.planned_datetime || "";
  const returnDeliveryTime = deliveryDate ? toInputDateTime(new Date(deliveryDate.getTime() + outboundMs).toISOString()) : "";
  const pickupReturn = { ...createStop("pickup"), ...delivery, stop_type: "pickup", planned_datetime: returnPickupTime, actual_arrived_at: "", actual_departed_at: "", driver_id: "", truck_id: "", trailer_id: "", auto_round_trip_time: true };
  const deliveryReturn = { ...createStop("delivery"), ...pickup, stop_type: "delivery", planned_datetime: returnDeliveryTime, actual_arrived_at: "", actual_departed_at: "", driver_id: "", truck_id: "", trailer_id: "", auto_round_trip_time: true };
  return [pickup, delivery, pickupReturn, deliveryReturn];
};